
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { RootState } from '../../../../store';
import ActionSheet from './ActionSheet';
import FiltersBottomSheet from './FiltersBottomSheet';
import CustomSelect from '../../../components/CustomSelect';
import {
    Specialty,
    Organization,
    SpecialtiesListResponse,
    getSpecialties,
    getOrganizations,
    createSpecialty,
    updateSpecialty,
    deleteSpecialty,
} from '../../../utils/adminApiUtils';
import { getSpecialtyProfessions } from '../../../utils/apiUtils';

const isSpecialtyActive = (s: Specialty): boolean => s.is_active !== false;

const SpecialtiesManager: React.FC = (): React.ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const currentUserRoleId = useSelector((state: RootState) => state.user.role_id);
    const isSuperAdmin = currentUserRoleId === 4;
    const [specialties, setSpecialties] = useState<Specialty[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [actionSheetOpen, setActionSheetOpen] = useState<number | null>(null);
    const [actionSheetPosition, setActionSheetPosition] = useState<{ x: number; y: number } | undefined>(undefined);
    const [showFiltersSheet, setShowFiltersSheet] = useState(false);

    // Фильтры и пагинация
    const [search, setSearch] = useState('');
    const [organizationFilter, setOrganizationFilter] = useState<number | ''>('');
    const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'updated_at'>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);

    // Форма
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        structure: '',
        organization_id: '' as number | '',
        profession: '',
    });
    const [professions, setProfessions] = useState<string[]>([]);
    const [loadingProfessions, setLoadingProfessions] = useState(false);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [submitting, setSubmitting] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(true);
    const [togglingSpecialtyId, setTogglingSpecialtyId] = useState<number | null>(null);

    // Загрузка организаций для фильтра
    const fetchOrganizations = useCallback(async () => {
        setLoadingOrganizations(true);
        try {
            const response = await getOrganizations({ limit: 1000, sort_by: 'name', sort_order: 'asc' });
            setOrganizations(response.organizations);
        } catch (err) {
            console.error('Ошибка загрузки организаций:', err);
        } finally {
            setLoadingOrganizations(false);
        }
    }, []);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    // Загрузка профессий
    useEffect(() => {
        setLoadingProfessions(true);
        getSpecialtyProfessions()
            .then(setProfessions)
            .catch(() => setProfessions([]))
            .finally(() => setLoadingProfessions(false));
    }, []);

    // Загрузка специальностей (silent: без полноэкранного loading — сохраняет скролл таблицы)
    const fetchSpecialties = useCallback(async (options?: { silent?: boolean }) => {
        const silent = options?.silent === true;
        try {
            if (!silent) {
                setLoading(true);
            }
            setError(null);

            const params: any = {
                limit,
                offset,
                sort_by: sortBy,
                sort_order: sortOrder,
            };

            if (search.trim()) {
                params.search = search.trim();
            }

            if (organizationFilter !== '' && typeof organizationFilter === 'number') {
                params.organization_id = organizationFilter;
            }

            if (isSuperAdmin && includeInactive) {
                params.include_inactive = true;
            }

            const response: SpecialtiesListResponse = await getSpecialties(params);
            setSpecialties(response.specialties);
            setTotal(response.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Ошибка загрузки специальностей');
            enqueueSnackbar(err instanceof Error ? err.message : 'Ошибка загрузки специальностей', { variant: 'error' });
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [limit, offset, search, organizationFilter, sortBy, sortOrder, includeInactive, isSuperAdmin, enqueueSnackbar]);

    useEffect(() => {
        fetchSpecialties();
    }, [fetchSpecialties]);

    // Сброс пагинации при изменении фильтров
    useEffect(() => {
        setOffset(0);
    }, [search, organizationFilter, sortBy, sortOrder, includeInactive]);

    const handleCreate = () => {
        setSelectedSpecialty(null);
        setDialogMode('create');
        setFormData({
            name: '',
            description: '',
            structure: '',
            organization_id: '',
            profession: '',
        });
        setFormErrors({});
        setShowDialog(true);
    };

    const handleEdit = (specialty: Specialty) => {
        setSelectedSpecialty(specialty);
        setDialogMode('edit');
        setFormData({
            name: specialty.name,
            description: specialty.description || '',
            structure: specialty.structure || '',
            organization_id: specialty.organization_id || '',
            profession: specialty.profession || '',
        });
        setFormErrors({});
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
        setSelectedSpecialty(null);
        setFormData({
            name: '',
            description: '',
            structure: '',
            organization_id: '',
            profession: '',
        });
        setFormErrors({});
    };

    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};
        if (!formData.name.trim()) {
            errors.name = 'Введите название специальности';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Название должно содержать минимум 2 символа';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const submitData: any = {
                name: formData.name.trim(),
            };

            if (formData.description.trim()) {
                submitData.description = formData.description.trim();
            }

            if (formData.structure.trim()) {
                submitData.structure = formData.structure.trim();
            }

            if (formData.organization_id !== '' && typeof formData.organization_id === 'number') {
                submitData.organization_id = formData.organization_id;
            }

            if (formData.profession.trim()) {
                submitData.profession = formData.profession.trim();
            }

            if (dialogMode === 'create') {
                await createSpecialty(submitData);
                enqueueSnackbar('Специальность успешно создана', { variant: 'success' });
            } else if (selectedSpecialty) {
                await updateSpecialty(selectedSpecialty.id, submitData);
                enqueueSnackbar('Специальность успешно обновлена', { variant: 'success' });
            }
            handleCloseDialog();
            fetchSpecialties();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Ошибка сохранения специальности';
            setFormErrors({ submit: message });
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteSpecialty(id);
            enqueueSnackbar('Специальность успешно удалена', { variant: 'success' });
            // Закрываем actionSheet и сбрасываем все состояния
            setActionSheetOpen(null);
            setDeleteConfirm(null);
            setActionSheetPosition(undefined);
            // Обновляем список после удаления
            fetchSpecialties();
        } catch (err) {
            enqueueSnackbar(err instanceof Error ? err.message : 'Ошибка удаления специальности', { variant: 'error' });
            // В случае ошибки тоже закрываем actionSheet
            setActionSheetOpen(null);
            setDeleteConfirm(null);
            setActionSheetPosition(undefined);
        }
    };

    const handleToggleSpecialtyActive = async (e: React.MouseEvent, specialty: Specialty) => {
        e.stopPropagation();
        const nextActive = !isSpecialtyActive(specialty);
        setTogglingSpecialtyId(specialty.id);
        try {
            await updateSpecialty(specialty.id, { is_active: nextActive });
            enqueueSnackbar(nextActive ? 'Специальность включена' : 'Специальность выключена', { variant: 'success' });
            await fetchSpecialties({ silent: true });
        } catch (err) {
            enqueueSnackbar(err instanceof Error ? err.message : 'Не удалось изменить статус', { variant: 'error' });
        } finally {
            setTogglingSpecialtyId(null);
        }
    };

    const handleDuplicate = async (specialty: Specialty) => {
        try {
            const duplicateData: any = {
                name: `${specialty.name} (копия)`,
            };

            if (specialty.description) {
                duplicateData.description = specialty.description;
            }

            if (specialty.structure) {
                duplicateData.structure = specialty.structure;
            }

            if (specialty.organization_id) {
                duplicateData.organization_id = specialty.organization_id;
            }

            if (specialty.profession) {
                duplicateData.profession = specialty.profession;
            }

            await createSpecialty(duplicateData);
            enqueueSnackbar('Копия специальности успешно создана', { variant: 'success' });
            setActionSheetOpen(null);
            fetchSpecialties();
        } catch (err) {
            enqueueSnackbar(err instanceof Error ? err.message : 'Ошибка создания копии специальности', { variant: 'error' });
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('ru-RU');
    };

    return (
        <div className="specialties-section">
            <div className="section-header">
                <h2>Управление специальностями</h2>
                <div className="specialties-section-header-actions">
                    {isSuperAdmin && (
                        <label className="include-inactive-toggle">
                            <input
                                type="checkbox"
                                checked={includeInactive}
                                onChange={(e) => setIncludeInactive(e.target.checked)}
                            />
                            <span>Показывать неактивные</span>
                        </label>
                    )}
                    <button className="button-primary" onClick={handleCreate}>
                        Добавить специальность
                    </button>
                </div>
            </div>

            <div className="organizations-controls">
                <div className="search-filters">
                    <input
                        type="text"
                        placeholder="Поиск по названию или описанию"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-input"
                    />

                    <button
                        type="button"
                        className="filters-button-mobile"
                        onClick={() => setShowFiltersSheet(true)}
                    >
                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                        Фильтры
                    </button>

                    <div className="filters-desktop">
                        <CustomSelect
                            value={organizationFilter === '' ? '' : organizationFilter.toString()}
                            onChange={(value) => setOrganizationFilter(value === '' ? '' : Number(value))}
                            className=""
                            placeholder="Все организации"
                            options={[
                                { value: '', label: 'Все организации' },
                                ...organizations.map((org) => ({
                                    value: org.id.toString(),
                                    label: org.name,
                                })),
                            ]}
                        />

                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as 'name' | 'created_at' | 'updated_at')}
                            className="role-filter"
                        >
                            <option value="name">Сортировка: Название</option>
                            <option value="created_at">Сортировка: Дата создания</option>
                            <option value="updated_at">Сортировка: Дата обновления</option>
                        </select>

                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                            className="role-filter"
                        >
                            <option value="asc">По возрастанию</option>
                            <option value="desc">По убыванию</option>
                        </select>

                        <select
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className="role-filter"
                        >
                            <option value={10}>10 записей</option>
                            <option value={25}>25 записей</option>
                            <option value={50}>50 записей</option>
                            <option value={100}>100 записей</option>
                        </select>
                    </div>
                </div>
            </div>

            <FiltersBottomSheet
                isOpen={showFiltersSheet}
                onClose={() => setShowFiltersSheet(false)}
                title="Фильтры"
            >
                <div className="filters-sheet-content">
                    <div className="form-group">
                        <label>Организация</label>
                        <CustomSelect
                            value={organizationFilter === '' ? '' : organizationFilter.toString()}
                            onChange={(value) => setOrganizationFilter(value === '' ? '' : Number(value))}
                            className=""
                            placeholder="Все организации"
                            options={[
                                { value: '', label: 'Все организации' },
                                ...organizations.map((org) => ({
                                    value: org.id.toString(),
                                    label: org.name,
                                })),
                            ]}
                        />
                    </div>

                    <div className="form-group">
                        <label>Сортировка</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as 'name' | 'created_at' | 'updated_at')}
                            className="role-filter"
                        >
                            <option value="name">Название</option>
                            <option value="created_at">Дата создания</option>
                            <option value="updated_at">Дата обновления</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Порядок сортировки</label>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                            className="role-filter"
                        >
                            <option value="asc">По возрастанию</option>
                            <option value="desc">По убыванию</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Количество записей</label>
                        <select
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className="role-filter"
                        >
                            <option value={10}>10 записей</option>
                            <option value={25}>25 записей</option>
                            <option value={50}>50 записей</option>
                            <option value={100}>100 записей</option>
                        </select>
                    </div>
                </div>
            </FiltersBottomSheet>

            {loading ? (
                <div className="loading">Загрузка...</div>
            ) : error ? (
                <div className="error-message">
                    <p>{error}</p>
                    <button type="button" onClick={() => fetchSpecialties()}>Попробовать снова</button>
                </div>
            ) : (
                <>
                    <div className="users-table-container">
                        <table className="admin-table users-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Название</th>
                                    <th>Специальность</th>
                                    <th>Описание</th>
                                    <th>Организация</th>
                                    <th>Статус</th>
                                    <th>Дата обновления</th>
                                </tr>
                            </thead>
                            <tbody>
                                {specialties.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                                            Специальности не найдены
                                        </td>
                                    </tr>
                                ) : (
                                    specialties.map((specialty) => (
                                        <tr
                                            key={specialty.id}
                                            onClick={(e) => {
                                                setActionSheetPosition({
                                                    x: e.clientX,
                                                    y: e.clientY,
                                                });
                                                setActionSheetOpen(specialty.id);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>{specialty.id}</td>
                                            <td>
                                                <span className="user-name">{specialty.name}</span>
                                            </td>
                                            <td>{specialty.profession || '—'}</td>
                                            <td>{specialty.description || '—'}</td>
                                            <td>{specialty.organization?.name || '—'}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    className={`activation-status-btn ${isSpecialtyActive(specialty) ? 'active' : 'inactive'}`}
                                                    disabled={togglingSpecialtyId === specialty.id}
                                                    onClick={(e) => handleToggleSpecialtyActive(e, specialty)}
                                                    title={isSpecialtyActive(specialty) ? 'Выключить специальность' : 'Включить специальность'}
                                                >
                                                    {togglingSpecialtyId === specialty.id
                                                        ? '…'
                                                        : isSpecialtyActive(specialty)
                                                            ? 'Активна'
                                                            : 'Выкл'}
                                                </button>
                                            </td>
                                            <td>{formatDate(specialty.updated_at)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {total > 0 && (
                        <div className="pagination pt-3">
                            <button
                                onClick={() => setOffset(Math.max(0, offset - limit))}
                                disabled={offset === 0}
                                className="pagination-btn"
                            >
                                ← Предыдущая
                            </button>
                            <span className="pagination-info">
                                Показано {offset + 1}-{Math.min(offset + limit, total)} из {total}
                            </span>
                            <button
                                onClick={() => setOffset(offset + limit)}
                                disabled={offset + specialties.length >= total}
                                className="pagination-btn"
                            >
                                Следующая →
                            </button>
                        </div>
                    )}
                </>
            )}

            {actionSheetOpen !== null && (
                <ActionSheet
                    isOpen={true}
                    position={actionSheetPosition}
                    onClose={() => {
                        setActionSheetOpen(null);
                        setDeleteConfirm(null);
                        setActionSheetPosition(undefined);
                    }}
                    options={[
                        {
                            label: 'Редактировать',
                            action: () => {
                                const specialty = specialties.find(s => s.id === actionSheetOpen);
                                if (specialty) handleEdit(specialty);
                            },
                        },
                        {
                            label: 'Сделать копию',
                            action: () => {
                                const specialty = specialties.find(s => s.id === actionSheetOpen);
                                if (specialty) handleDuplicate(specialty);
                            },
                        },
                        {
                            label: deleteConfirm === actionSheetOpen ? 'Подтвердить удаление' : 'Удалить',
                            action: async () => {
                                if (deleteConfirm === actionSheetOpen) {
                                    // Подтверждение удаления - вызываем handleDelete
                                    await handleDelete(actionSheetOpen);
                                } else {
                                    // Первое нажатие - запрашиваем подтверждение
                                    setDeleteConfirm(actionSheetOpen);
                                    // Не закрываем actionSheet, просто обновляем label
                                }
                            },
                            variant: 'danger',
                            shouldClose: deleteConfirm === actionSheetOpen, // Закрываем только при подтверждении удаления
                        },
                    ]}
                />
            )}

            {showDialog && (
                <div className="dialog-overlay" onClick={handleCloseDialog}>
                    <div className="user-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3>{dialogMode === 'create' ? 'Добавить специальность' : 'Редактировать специальность'}</h3>
                            <button
                                type="button"
                                className="dialog-close-button"
                                onClick={handleCloseDialog}
                                aria-label="Закрыть"
                            >
                                ×
                            </button>
                        </div>

                        <div className="user-dialog-content">
                            <form onSubmit={handleSubmit} className="d-flex flex-row gap-3">
                                <div className="w-50 d-flex flex-column justify-content-between">
                                    <div className="">
                                        <div className="form-group">
                                            <label>Название *</label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, name: e.target.value });
                                                    if (formErrors.name) {
                                                        setFormErrors({ ...formErrors, name: '' });
                                                    }
                                                }}
                                                className={formErrors.name ? 'error' : ''}
                                                placeholder="Введите название специальности"
                                            />
                                            {formErrors.name && <div className="error-text">{formErrors.name}</div>}
                                        </div>

                                        <div className="form-group">
                                            <label>Специальность</label>
                                            {loadingProfessions ? (
                                                <div style={{ padding: '12px', color: '#666' }}>Загрузка...</div>
                                            ) : (
                                                <CustomSelect
                                                    value={formData.profession}
                                                    onChange={(value) => setFormData({ ...formData, profession: value.toString() })}
                                                    className="admin-style"
                                                    placeholder="Выберите профессию"
                                                    options={[
                                                        { value: '', label: 'Без профессии' },
                                                        ...(formData.profession && !professions.includes(formData.profession)
                                                            ? [{ value: formData.profession, label: formData.profession }]
                                                            : []),
                                                        ...professions.map((p) => ({ value: p, label: p })),
                                                    ]}
                                                    searchable
                                                    searchPlaceholder="Поиск профессии..."
                                                />
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label>Организация</label>
                                            {loadingOrganizations ? (
                                                <div style={{ padding: '12px', color: '#666' }}>Загрузка организаций...</div>
                                            ) : (
                                                <CustomSelect
                                                    value={formData.organization_id === '' ? '' : formData.organization_id.toString()}
                                                    onChange={(value) =>
                                                        setFormData({
                                                            ...formData,
                                                            organization_id: value === '' ? '' : Number(value),
                                                        })
                                                    }
                                                    className="admin-style"
                                                    placeholder="Выберите организацию"
                                                    options={[
                                                        { value: '', label: 'Без организации' },
                                                        ...organizations.map((org) => ({
                                                            value: org.id.toString(),
                                                            label: org.name,
                                                        })),
                                                    ]}
                                                />
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label>Описание</label>
                                            <input
                                                type="text"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Краткое описание специальности (опционально)"
                                            />
                                        </div>
                                    </div>
                                    <div className="dialog-actions-fixed">
                                        <button type="button" className="secondary" onClick={handleCloseDialog}>
                                            Отмена
                                        </button>
                                        <button
                                            type="button"
                                            className="primary"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Сохранение...' : dialogMode === 'create' ? 'Создать' : 'Сохранить'}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group w-100 hv-100">
                                    <label>Структура протокола</label>
                                    <textarea
                                        value={formData.structure}
                                        onChange={(e) => setFormData({ ...formData, structure: e.target.value })}
                                        placeholder="Большое описание протокола (опционально)"

                                        className="protocol-structure-textarea"
                                    />
                                </div>

                                {formErrors.submit && <div className="error-text">{formErrors.submit}</div>}
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecialtiesManager;
